import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import './MembersManagement.css';

const MembersManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterInterest, setFilterInterest] = useState('all');
  const [sortBy, setSortBy] = useState('joinedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await adminService.getMembers();
      setMembers(res?.members ?? []);
    } catch (error) {
      console.error('Error fetching members:', error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar y ordenar miembros
  const filteredAndSortedMembers = members
    .filter(member => {
      const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           member.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesInterest = filterInterest === 'all' || member.interest === filterInterest;
      return matchesSearch && matchesInterest;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'joinedAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Obtener estadísticas
  const stats = {
    total: members.length,
    byInterest: members.reduce((acc, member) => {
      acc[member.interest] = (acc[member.interest] || 0) + 1;
      return acc;
    }, {}),
    recent: members.filter(member => {
      const memberDate = new Date(member.joinedAt);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return memberDate > oneWeekAgo;
    }).length
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener nombre del interés
  const getInterestName = (interest) => {
    const interestNames = {
      'coliving': 'Coliving',
      'webdesign': 'Web Design',
      'community': 'Comunidad'
    };
    return interestNames[interest] || interest;
  };

  if (loading) {
    return (
      <div className="members-management">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Cargando miembros de la comunidad...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="members-management">
      <div className="container">
        <div className="section-header">
          <h2>👥 Gestión de Miembros</h2>
          <p>Administra y visualiza los miembros de tu comunidad</p>
        </div>

        {/* Estadísticas */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total de Miembros</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-calendar-week"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.recent}</h3>
              <p>Última Semana</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-chart-pie"></i>
            </div>
            <div className="stat-content">
              <h3>{Object.keys(stats.byInterest).length}</h3>
              <p>Áreas de Interés</p>
            </div>
          </div>
        </div>

        {/* Controles */}
        <div className="admin-controls">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-controls">
            <select
              value={filterInterest}
              onChange={(e) => setFilterInterest(e.target.value)}
            >
              <option value="all">Todas las áreas</option>
              <option value="coliving">Coliving</option>
              <option value="webdesign">Web Design</option>
              <option value="community">Comunidad</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="joinedAt">Fecha de registro</option>
              <option value="name">Nombre</option>
              <option value="interest">Área de interés</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-btn"
            >
              <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
              {sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}
            </button>
          </div>
        </div>

        {/* Tabla de miembros */}
        <div className="members-table-container">
          {filteredAndSortedMembers.length === 0 ? (
            <div className="no-members">
              <i className="fas fa-inbox"></i>
              <h3>No hay miembros para mostrar</h3>
              <p>Los registros aparecerán aquí una vez que las personas se unan a la comunidad.</p>
            </div>
          ) : (
            <table className="members-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Área de Interés</th>
                  <th>Mensaje</th>
                  <th>Fecha de Registro</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedMembers.map((member) => (
                  <tr key={member.id}>
                    <td className="member-name">{member.name}</td>
                    <td className="member-email">{member.email}</td>
                    <td className="member-interest">
                      <span className={`interest-badge ${member.interest}`}>
                        {getInterestName(member.interest)}
                      </span>
                    </td>
                    <td className="member-message">
                      {member.message ? (
                        <div className="message-preview" title={member.message}>
                          {member.message.length > 50 
                            ? `${member.message.substring(0, 50)}...` 
                            : member.message
                          }
                        </div>
                      ) : (
                        <span className="no-message">Sin mensaje</span>
                      )}
                    </td>
                    <td className="member-date">{formatDate(member.joinedAt)}</td>
                    <td className="member-status">
                      <span className={`status-badge ${member.status}`}>
                        {member.status === 'pending' ? 'Pendiente' : 'Activo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Información de filtros */}
        <div className="filter-info">
          <p>
            Mostrando {filteredAndSortedMembers.length} de {members.length} miembros
            {filterInterest !== 'all' && ` en ${getInterestName(filterInterest)}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MembersManagement;



























